// store.ts — typed front-end wrappers over the on-device Rust SQLite + walker commands.
// Every call hits LOCAL state via Tauri IPC (durable, offline, on-disk). The whole repository NEVER
// leaves the device — the folder is walked + chunked + indexed ON-DEVICE; only the few matched
// code snippets + the question go to the brain (see brain.ts).
//
// Data model:
//   repo     { id, name, root, file_count, chunk_count, symbol_count, byte_count, created_at, updated_at }
//   file     { repo_id, path, lang, line_count, byte_count }
//   snippet  { repo_id, path, lang, start_line, end_line, code }   ← the local retrieval result
//   symbol   { id, repo_id, kind, name, path, line, lang }         ← parsed on-device in Rust
//   history  { id, repo_id, repo_name, question, answer, citations, created_at }

import { invoke } from "./tauri-web";

export interface Repo {
  id: string;
  /** The folder's basename (display). */
  name: string;
  /** Absolute root path on this device (on-device only; for re-index + provenance). */
  root: string;
  file_count: number;
  chunk_count: number;
  symbol_count: number;
  byte_count: number;
  created_at: string;
  updated_at: string;
}

/** One indexed file inside a repo. `path` is RELATIVE to the repo root (the citation handle). */
export interface FileRow {
  repo_id: string;
  path: string;
  lang: string;
  line_count: number;
  byte_count: number;
}

/** One matched code snippet — the ONLY repo content that leaves the device (top ~10 per question). */
export interface Snippet {
  repo_id: string;
  path: string;
  lang: string;
  /** 1-based inclusive line range, for citations ("src/auth.ts:40-58"). */
  start_line: number;
  end_line: number;
  code: string;
}

/** One parsed symbol — derived deterministically in Rust at index time (walker.rs). */
export interface Symbol {
  id: string;
  repo_id: string;
  /** "function" | "class" | "struct" | "interface" | "type" | "enum" | "method". */
  kind: SymbolKind;
  name: string;
  /** File path (relative) + 1-based declaration line — the citation. */
  path: string;
  line: number;
  lang: string;
}
export interface Freshness {
  changed: number;
  scanned: number;
  newest: string | null;
  examples: string[];
  /** the folder itself can no longer be read — a DIFFERENT state from stale, never render as fine */
  unreadable: boolean;
}

export type SymbolKind = "function" | "class" | "struct" | "interface" | "type" | "enum" | "method";

/** The summary returned by an on-device index pass (drives the progress/summary view). */
export interface IndexSummary {
  repo: Repo;
  /** Files seen but skipped (binary / oversized / unindexed extension) — UI honesty. */
  skipped: number;
}

/** One stored Q&A history row. */
export interface HistoryEntry {
  id: string;
  repo_id: string;
  repo_name: string;
  question: string;
  answer: string;
  /** JSON array string of cited file paths. */
  citations: string;
  created_at: string;
}

export const store = {
  listRepos: () => invoke<Repo[]>("list_repos"),
  /** Row 2b — is this index still current? Compares the folder on disk against when we indexed it.
   *  Metadata only (mtime), same SKIP_DIRS/lang filters as the indexer, so node_modules churn and
   *  lockfiles can't make every repo look permanently stale. */
  repoFreshness: (repoId: string) => invoke<Freshness>("repo_freshness", { repoId }),
  getRepo: (id: string) => invoke<Repo | null>("get_repo", { id }),
  repoCount: () => invoke<number>("repo_count"),
  /** Point the app at a LOCAL FOLDER and index it ON-DEVICE (walk + chunk + symbol-parse in Rust). */
  indexFolder: (root: string, name?: string) =>
    invoke<IndexSummary>("index_folder", { root, name }),
  /** Web shape: the browser can't walk a path, so the picked files ride along. */
  indexFolderWeb: (name: string, files: { path: string; text: string }[]) =>
    invoke<IndexSummary>("index_folder", { root: `web:${name}`, name, files }),
  deleteRepo: (id: string) => invoke<void>("delete_repo", { id }),
  listFiles: (repoId: string, limit = 200) =>
    invoke<FileRow[]>("list_files", { repoId, limit }),
  languageBreakdown: (repoId: string) =>
    invoke<[string, number][]>("language_breakdown", { repoId }),
  /** Local FTS5 code retrieval — the step that runs BEFORE the single brain call. */
  searchCode: (repoId: string, query: string, limit = 10) =>
    invoke<Snippet[]>("search_code", { repoId, query, limit }),
  /** Deterministic symbol lookup ("where is X defined?") — answered on-device, never by the LLM. */
  findSymbols: (repoId: string, query: string, limit = 20) =>
    invoke<Symbol[]>("find_symbols", { repoId, query, limit }),
  allSymbols: (repoId: string, limit = 300) =>
    invoke<Symbol[]>("all_symbols", { repoId, limit }),
  addHistory: (repoId: string, repoName: string, question: string, answer: string, citations: string[]) =>
    invoke<HistoryEntry>("add_history", { repoId, repoName, question, answer, citations }),
  listHistory: (limit = 100) => invoke<HistoryEntry[]>("list_history", { limit }),
  deleteHistory: (id: string) => invoke<void>("delete_history", { id }),
  clearHistory: () => invoke<void>("clear_history"),
};

// ── Pure policy / presentation helpers ─────────────────────────────────────────

/** A small label per language token. */
export const LANG_LABELS: Record<string, string> = {
  rust: "Rust", ts: "TypeScript", js: "JavaScript", python: "Python", go: "Go", java: "Java",
  kotlin: "Kotlin", swift: "Swift", c: "C", cpp: "C++", csharp: "C#", ruby: "Ruby", php: "PHP",
  scala: "Scala", dart: "Dart", lua: "Lua", shell: "Shell", sql: "SQL", html: "HTML", css: "CSS",
  vue: "Vue", svelte: "Svelte", json: "JSON", yaml: "YAML", toml: "TOML", xml: "XML",
  markdown: "Markdown", docker: "Docker", make: "Make", cmake: "CMake", text: "Text",
};
export const langLabel = (l: string): string => LANG_LABELS[l] ?? l.toUpperCase();

/** A short kind label + a small marker for the symbol map. */
export const SYMBOL_KIND_LABELS: Record<SymbolKind, string> = {
  function: "fn", method: "fn", class: "class", struct: "struct",
  interface: "iface", type: "type", enum: "enum",
};

/** Human file size. */
export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Human count, e.g. 1,240. */
export const fmtCount = (n: number): string => n.toLocaleString();

/** "src/auth/login.ts:40-58" citation form for a snippet. */
export const snippetCite = (s: { path: string; start_line: number; end_line: number }): string =>
  s.start_line === s.end_line ? `${s.path}:${s.start_line}` : `${s.path}:${s.start_line}-${s.end_line}`;

/** Pretty relative time for the history list, deterministic + client-side. */
export function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const secs = Math.round((Date.now() - t) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Parse the citations JSON column to a string[] safely. */
export function parseCitations(json: string): string[] {
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
